<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import AuthShowcase from "@/components/auth/AuthShowcase.vue";
import { useBreakpoints } from "@/composables/common/useBreakpoint";

const WIDE_LAYOUT_MIN_WIDTH = 1200;
const FORM_BOTTOM_GAP = 16;

const route = useRoute();
const { width } = useBreakpoints();

const root = ref(null);
const panel = ref(null);
const content = ref(null);
const showcaseMaxHeight = ref(null);

const isWide = computed(() => width.value >= WIDE_LAYOUT_MIN_WIDTH);
const isRouteResolved = computed(() => route.matched.length > 0);
const currentYear = computed(() => new Date().getFullYear());

function updateShowcaseMaxHeight() {
  if (isWide.value || !content.value) {
    showcaseMaxHeight.value = null;
    return;
  }

  const formHeight =
    content.value.getBoundingClientRect().bottom -
    panel.value.getBoundingClientRect().top;

  showcaseMaxHeight.value = Math.floor(
    root.value.clientHeight - formHeight - FORM_BOTTOM_GAP,
  );
}

const resizeObserver = new ResizeObserver(updateShowcaseMaxHeight);

watch(
  content,
  (element) => {
    if (element) {
      resizeObserver.observe(element);
      updateShowcaseMaxHeight();
    }
  },
  { flush: "post" },
);

onMounted(() => {
  resizeObserver.observe(root.value);
});

onBeforeUnmount(() => {
  resizeObserver.disconnect();
});
</script>

<template>
  <div ref="root" class="auth-layout" :class="{ 'auth-layout--wide': isWide }">
    <main v-if="isRouteResolved" class="auth-layout__inner">
      <AuthShowcase
        class="auth-layout__showcase"
        :compact="!isWide"
        :max-height="showcaseMaxHeight"
      />

      <section ref="panel" class="auth-layout__panel">
        <div ref="content" class="auth-layout__content">
          <router-view />
        </div>

        <footer class="auth-layout__footer">
          © {{ currentYear }}, зроблено з 🫶🏻 у Дніпрі
        </footer>
      </section>
    </main>
  </div>
</template>

<style lang="scss" scoped>
.auth-layout {
  height: 100%;
  overflow-y: auto;
  background: var(--forest-800);

  &__inner {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  &__showcase {
    flex-shrink: 0;
  }

  &__panel {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 24px;
    padding: 24px 24px 16px;
    border-radius: 12px 12px 0 0;
    background: #fff;
    box-shadow:
      0 14px 36px -10px rgba(15, 31, 25, 0.12),
      0 3px 8px rgba(15, 31, 25, 0.08);
  }

  &__content {
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
  }

  &__footer {
    margin-top: auto;
    font-size: 0.875rem;
    line-height: 1.375rem;
    color: var(--gray-400);
    text-align: center;
  }

  &--wide {
    .auth-layout__inner {
      flex-direction: row;
    }

    .auth-layout__showcase {
      position: sticky;
      top: 0;
      flex-grow: 1;
      height: 100vh;
      height: 100dvh;
    }

    .auth-layout__panel {
      flex: 0 0 432px;
      padding: 48px 56px 16px;
      border-radius: 12px 0 0 12px;
    }

    .auth-layout__content {
      max-width: none;
    }
  }
}
</style>
